import React from 'react';
import PropTypes from 'prop-types';
import {
  MotifButton,
  MotifModal,
  MotifModalHeader,
  MotifModalBody,
  MotifModalFooter,
  MotifFileUploader,
  MotifTextLink,
} from '@ey-xd/motif-react';
import * as Constants from '../../constants/other';
import * as masterDataManagementActions from '../../actions/masterDataManagementActions';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from '../../common/withRouter';
import { Link } from 'react-router';
import { Item } from 'semantic-ui-react';

class UploadREITsModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      fileStatuses: [],
      validationMessages: '',
    };
    this.onDrop = this.onDrop.bind(this);
    this.renderUploadProgress = this.renderUploadProgress.bind(this);
  }

  componentWillUnmount() {
    // Ensure scroll is restored if component unmounts
    document.body.style.overflow = 'auto';
  }

  /**
   * Handles the file drop event.
   * @param {Array} acceptedFiles - The accepted files.
   * @param {Array} rejectedFiles - The rejected files.
   */
  onDrop(acceptedFiles, rejectedFiles) {
    const addedStatuses = [];

    // Handle accepted files
    if (acceptedFiles && acceptedFiles.length === 1) {
      addedStatuses.push({
        file: acceptedFiles[0],
        status: 'IN_PROGRESS',
      });

      this.props.masterDataManagementActions
        .uploadREITTemplate(this.props.clientId, acceptedFiles[0])
        .then((response) => {
          const addedStatuses = [];
          if (response && response.success) {
            addedStatuses.push({
              file: acceptedFiles[0],
              status: 'UPLOADED',
            });
            this.setState({ fileStatuses: addedStatuses });
          } else if (
            response &&
            response.success === false &&
            response.fileUploadId <= 0
          ) {
            addedStatuses.push({
              file: acceptedFiles[0],
              status: 'ERROR',
            });
            this.setState({
              fileStatuses: addedStatuses,
              validationMessages: response.displayMessage,
            });
          } else if (
            response &&
            response.success === false &&
            response.fileUploadId > 0
          ) {
            addedStatuses.push({
              file: acceptedFiles[0],
              status: 'VALIDATION_ERROR',
              fileUploadId: response.fileUploadId,
              clientId: response.clientID,
            });
            this.setState({
              fileStatuses: addedStatuses,
              validationMessages: response.displayMessage,
            });
          }
        })
        .catch((error) => {
          // Handle errors including IIS timeout
          const errorStatuses = [
            {
              file: acceptedFiles[0],
              status: 'ERROR',
            },
          ];
          this.setState({
            fileStatuses: errorStatuses,
            validationMessages:
              error?.message ||
              'Upload failed due to an unexpected error. Please try the upload again.',
          });
        });
    }

    // Handle rejected files
    if (rejectedFiles && rejectedFiles.length > 0) {
      // Check if all rejected files are due to too many files (this is specific to the dropping of multiple files)
      if (
        rejectedFiles?.every((e) =>
          e?.errors.map((c) => c.code)?.find((err) => err === 'too-many-files')
        )
      ) {
        addedStatuses.push({
          file: null,
          status: 'REJECTED',
        });
      } else {
        // Handle individual rejected files to display the file name
        rejectedFiles.forEach((rejectedFile) => {
          if (
            rejectedFile.errors.find((err) => err.code)?.code ===
              'file-invalid-type' ||
            rejectedFile.errors.find((err) => err.code)?.code ===
              'file-too-large'
          ) {
            addedStatuses.push({
              file: rejectedFile.file,
              status: 'REJECTED',
              errorType: rejectedFile.errors.find((err) => err.code)?.code,
            });
          }
        });
      }
    }

    // Update state with new file statuses
    this.setState({ fileStatuses: addedStatuses });
  }

  handleMDMREITFailureExport(clientId, fileUploadId) {
    this.props.masterDataManagementActions.exportREITsErrorFile(
      clientId,
      fileUploadId
    );
  }

  /**
   * Renders the upload progress.
   * @returns {JSX.Element} The upload progress elements.
   */
  renderUploadProgress() {
    const getClassNameAndMessage = (
      status,
      fileSize,
      errorType,
      clientId,
      fileUploadId
    ) => {
      if (status === 'PENDING') {
        return { className: 'row', message: 'Pending Upload' };
      }

      if (status === 'ERROR') {
        return {
          className: 'row bg-danger text-white',
          message: this.state.validationMessages,
        };
      }

      if (status === 'IN_PROGRESS') {
        return {
          className: 'row bg-secondary text-white',
          message: 'File uploaded successfully. Import in progress.',
        };
      }

      if (status === 'VALIDATION_ERROR') {
        return {
          className: 'row bg-danger text-white',
          message: (
            <>
              Upload failed. Click
              <Link
                href="#"
                data-testid="lnkClickhere"
                onClick={(e) => {
                  e.preventDefault();
                  this.handleMDMREITFailureExport(clientId, fileUploadId);
                }}
              >
                <MotifTextLink variant="alt" id="mdm-text-link">
                  &nbsp;here
                </MotifTextLink>
              </Link>{' '}
              to download the file with validation results.
            </>
          ),
        };
      }

      if (status === 'UPLOADED') {
        return {
          className: 'row bg-success text-white',
          message: 'Import successful.',
        };
      }

      if (status === 'REJECTED') {
        let displayMessage = '';

        if (errorType === 'file-invalid-type') {
          displayMessage = 'Invalid file extension.';
        } else if (fileSize > Constants.MAX_UPLOAD_FILE_SIZE_IN_BYTES) {
          displayMessage =
            'The file size has exceeded max limit of ' +
            Constants.MAX_UPLOAD_FILE_SIZE_IN_MB +
            'MB.';
        }

        return {
          className: 'row bg-danger text-white',
          message: displayMessage,
        };
      }

      return { className: 'row bg-warning', message: 'Status Unknown' };
    };

    return this.state.fileStatuses.map(
      (item, index) =>
        item.file !== null && (
          <div
            key={index}
            className={
              getClassNameAndMessage(
                item.status,
                item.file?.size,
                item?.errorType,
                item?.clientId,
                item?.fileUploadId
              ).className
            }
          >
            <div
              className="col-md-6"
              style={{ display: 'inline-block' }}
              data-testid="divFileName"
            >
              {item.file?.name || 'Unknown File'}
            </div>
            <div className="col-md-5 offset-md-1" data-testid="divSuccessMsg">
              {
                getClassNameAndMessage(
                  item.status,
                  item.file?.size,
                  item?.errorType,
                  item?.clientId,
                  item?.fileUploadId
                ).message
              }
            </div>
          </div>
        )
    );
  }

  /**
   * Renders the UploadREITsModal component.
   * @returns {JSX.Element} The rendered component.
   */
  render() {
    return (
      <MotifModal
        variant="alt"
        size="lg"
        show={this.props.show}
        onClose={this.props.onClose}
        data-testid="divUploadReitsModal"
      >
        <MotifModalHeader
          closeButtonProps={this.props.onClose}
          data-testid="mdhdrUploadReits"
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'start',
              justifyContent: 'center',
            }}
          >
            <span className="ml-2">Upload REITs</span>
          </div>
        </MotifModalHeader>
        <MotifModalBody>
          <MotifFileUploader
            onDrop={this.onDrop}
            onDropRejected={(e) => this.onDrop(null, e)}
            label="Please upload a single file using the approved REITs import template."
            maxSize={Constants.MAX_UPLOAD_FILE_SIZE_IN_BYTES}
            accept={{ 'application/vnd.ms-excel': ['.xlsx'] }}
            multiple={false}
            maxFiles={1}
            data-test-id="fileInput"
            tabIndex="-1"
            className={
              this.state.fileStatuses?.some(
                (status) => status.status === 'REJECTED'
              )
                ? 'motif-invalid'
                : ''
            }
          />
          <div className="react-modal-content-scrollable mt-3 mb-3">
            {this.renderUploadProgress()}
          </div>
        </MotifModalBody>
        <MotifModalFooter>
          <MotifButton
            variant="primary"
            className="right"
            type="button"
            data-testid="btnClose"
            onClick={this.props.onClose}
          >
            Close
          </MotifButton>
        </MotifModalFooter>
      </MotifModal>
    );
  }
}

UploadREITsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onDrop: PropTypes.func,
  fileStatuses: PropTypes.array,
  renderUploadProgress: PropTypes.func,
  clientId: PropTypes.number.isRequired,
  masterDataManagementActions: PropTypes.object.isRequired,
};

/**
 * Maps items from state to properties of the component
 * @param {Object} state The state
 * @returns {Object} An object containing properties that the component can access
 */
function mapStateToProps(state, ownProps) {
  let cid = Number.parseInt(ownProps.router.params.clientId);
  if (isNaN(cid)) {
    cid = 0;
  }
  return {
    clientId: cid,
  };
}

/**
 * Binds actions to the dispatcher
 * @param {Object} dispatch The action dispatcher
 * @returns {Object} An object containing properties that the component can access
 */
function mapDispatchToProps(dispatch) {
  return {
    masterDataManagementActions: bindActionCreators(
      masterDataManagementActions,
      dispatch
    ),
  };
}

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(UploadREITsModal)
);
